import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { CopyIcon } from 'lucide-react';
import { useCopyToClipboard } from 'usehooks-ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { Button } from '../button.tsx';
import { DropdownMenuItem } from '../dropdown-menu.tsx';
import { useToast } from '../use-toast.tsx';

type CopyButtonProps = {
    className?: string;
    title?: string;
    textToCopy: string;
};

export const CopyButton: React.FC<CopyButtonProps> = ({ className, textToCopy, title }) => {
    const { t } = useLocale();
    const { mutate: onCopy } = useCopy();

    return (
        <Button
            className={className}
            icon={CopyIcon}
            variant={title === '' ? 'icon' : 'subtle'}
            onClick={() => {
                onCopy(textToCopy);
            }}
        >
            {title === undefined
                ? t('copy_id', {
                      ns: 'common',
                  })
                : title}
        </Button>
    );
};

type DropdownMenuItemCopyProps = {
    textToCopy: string;
    className?: string;
};

export const DropdownMenuItemCopy: React.FC<DropdownMenuItemCopyProps> = ({ textToCopy, className }) => {
    const { t } = useLocale();
    const { mutate: onCopy } = useCopy();

    return (
        <DropdownMenuItem
            onSelect={() => {
                onCopy(textToCopy);
            }}
            className={className}
        >
            <CopyIcon className="h-4 w-4" />
            <span>{t('copy_id', { ns: 'common' })}</span>
        </DropdownMenuItem>
    );
};

function useCopy(): UseMutationResult<boolean, unknown, string> {
    const [, copy] = useCopyToClipboard();
    const { toast } = useToast();
    const { t } = useLocale();

    return useMutation({
        mutationFn: copy,
        onError: () => {
            toast({
                icon: 'warning',
                title: t('copy_failed', { ns: 'toasts' }),
            });
        },
        onSuccess: () => {
            toast({
                icon: 'success',
                title: t('copy_successful', { ns: 'toasts' }),
            });
        },
    });
}
